import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NONE = "none";
const EMPTY = {
  name: "", email: "", password: "", employee_id: "", phone: "", role: "", team_id: NONE, reports_to: NONE,
  shift_id: NONE, joining_date: "", department: "", designation: "", employment_status: "active",
};

export function usePeopleOptions(open) {
  const [options, setOptions] = useState({ teams: [], shifts: [], people: [], roles: [] });
  useEffect(() => {
    if (open) api.get("/hrms/people/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, [open]);
  return options;
}

function Pick({ label, value, onChange, items, testId, allowNone = true }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value={NONE}>None</SelectItem>}
          {items.map(([key, text]) => <SelectItem key={key} value={key}>{text}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Text({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1" type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

const opt = (list, key = "id", text = "name") => list.map((item) => [item[key], item[text]]);
const orNull = (value) => (value === NONE ? null : value);

export function EmployeeOnboardDialog({ open, onClose, onSaved }) {
  const options = usePeopleOptions(open);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  useEffect(() => { if (open) setForm(EMPTY); }, [open]);

  const submit = async () => {
    const missing = [["name", "Name"], ["email", "Email"], ["password", "Temporary password"], ["employee_id", "Employee ID"], ["role", "Role"]]
      .filter(([key]) => !String(form[key]).trim()).map(([, label]) => label);
    if (missing.length) return toast.error(`Required: ${missing.join(", ")}`);
    if (form.password.length < 8) return toast.error("Temporary password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/hrms/employees/onboard", {
        ...form, team_id: orNull(form.team_id), reports_to: orNull(form.reports_to), shift_id: orNull(form.shift_id),
        joining_date: form.joining_date || null,
      });
      toast.success(`${form.name} added — they can sign in with ${form.email}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="employee-onboard-dialog">
        <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Text label="Full name *" value={form.name} onChange={set("name")} testId="onboard-name" />
          <Text label="Employee ID *" value={form.employee_id} onChange={set("employee_id")} testId="onboard-employee-id" />
          <Text label="Work email (login) *" type="email" value={form.email} onChange={set("email")} testId="onboard-email" />
          <Text label="Temporary password *" type="password" value={form.password} onChange={set("password")} testId="onboard-password" />
          <Text label="Phone" value={form.phone} onChange={set("phone")} testId="onboard-phone" />
          <Pick label="Role *" value={form.role || undefined} onChange={set("role")} items={opt(options.roles, "key", "label")} testId="onboard-role" allowNone={false} />
          <Pick label="Team" value={form.team_id} onChange={set("team_id")} items={opt(options.teams)} testId="onboard-team" />
          <Pick label="Reports to" value={form.reports_to} onChange={set("reports_to")} items={opt(options.people)} testId="onboard-reports-to" />
          <Pick label="Shift" value={form.shift_id} onChange={set("shift_id")} items={opt(options.shifts)} testId="onboard-shift" />
          <Text label="Joining date" type="date" value={form.joining_date} onChange={set("joining_date")} testId="onboard-joining-date" />
          <Text label="Department" value={form.department} onChange={set("department")} testId="onboard-department" />
          <Text label="Designation" value={form.designation} onChange={set("designation")} testId="onboard-designation" />
          <Pick label="Status" value={form.employment_status} onChange={set("employment_status")} allowNone={false}
            items={[["active", "Active"], ["probation", "Probation"], ["notice", "Notice period"], ["inactive", "Inactive"]]} testId="onboard-status" />
        </div>
        <p className="text-xs text-muted-foreground">If no "Reports to" is chosen, the employee reports to their team's lead.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="onboard-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="onboard-submit">Add employee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssignmentDialog({ targets, onClose, onSaved }) {
  const open = !!targets?.length;
  const options = usePeopleOptions(open);
  const single = targets?.length === 1 ? targets[0] : null;
  const [form, setForm] = useState({ team_id: "", reports_to: "", shift_id: "" });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({ team_id: "", reports_to: "", shift_id: "" });
    if (single?.user_id) api.get(`/hrms/people/${single.user_id}/assignment-history`).then(({ data }) => setHistory(data.items)).catch(() => setHistory([]));
    else setHistory([]);
  }, [open, single?.user_id]);

  const submit = async () => {
    const fields = Object.keys(form).filter((key) => form[key]);
    if (!fields.length) return toast.error("Choose at least one change");
    const payload = { fields, ...Object.fromEntries(fields.map((key) => [key, orNull(form[key])])) };
    setSaving(true);
    try {
      if (single) {
        await api.put(`/hrms/people/${single.user_id}/assignment`, payload);
        toast.success("Assignment saved");
      } else {
        const { data } = await api.post("/hrms/people/bulk-assignment", { ...payload, user_ids: targets.map((t) => t.user_id) });
        toast[data.errors.length ? "warning" : "success"](`${data.updated} updated${data.errors.length ? `, ${data.errors.length} failed: ${data.errors[0].error}` : ""}`);
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="employee-assignment-dialog">
        <DialogHeader><DialogTitle>{single ? `Assign ${single.name || "employee"}` : `Bulk reassign ${targets?.length} employees`}</DialogTitle></DialogHeader>
        {single && (
          <div className="text-xs text-muted-foreground">Now: {single.team_name || "No team"} · reports to {single.reports_to_name || "—"} · {single.shift_name || "No shift"}</div>
        )}
        <div className="grid gap-3">
          <Pick label="Team" value={form.team_id || undefined} onChange={set("team_id")} items={opt(options.teams)} testId="assign-team" />
          <Pick label="Reports to" value={form.reports_to || undefined} onChange={set("reports_to")} items={opt(options.people)} testId="assign-reports-to" />
          <Pick label="Shift" value={form.shift_id || undefined} onChange={set("shift_id")} items={opt(options.shifts)} testId="assign-shift" />
        </div>
        {single && (
          <div className="mt-2">
            <div className="text-sm font-semibold">Change history</div>
            <div className="mt-1 max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground" data-testid="assignment-history">
              {history.map((h) => (
                <div key={h.id}>{new Date(h.at).toLocaleString()} · {h.field.replace(/_id$/, "").replace("_", " ")}: {h.from_label || "—"} → {h.to_label || "—"} · by {h.by_name}</div>
              ))}
              {!history.length && <div>No assignment changes yet.</div>}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="assign-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="assign-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
